"use client";

import { SiteTemplatePageSectionTypeTechnologyDefaultListItemSettingModel } from "@/Apis/ClinicApi";
import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { TechnologySectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeThirteenTechnologySectionGenerated, {
  HomeThirteenTechnologySectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenTechnologySection.generated";
import HomeThreeTechnologySectionGenerated, {
  HomeThreeTechnologySectionGeneratedProps,
} from "../Sections/_generated/HomeThreeTechnologySection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedTechnologyRenderer = (
  props: TechnologySectionProps,
) => ReactElement;

const TechnologyAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateTechnologyAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getListItems(
  props: TechnologySectionProps,
): SiteTemplatePageSectionTypeTechnologyDefaultListItemSettingModel[] {
  return props.lists.Default ?? [];
}

function getSectionImageUrl(props: TechnologySectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: TechnologySectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

function getItemImageUrl(
  item?: SiteTemplatePageSectionTypeTechnologyDefaultListItemSettingModel | null,
): string | undefined {
  return item?.Image?.Url ?? undefined;
}

function getItemImageAlt(
  item:
    | SiteTemplatePageSectionTypeTechnologyDefaultListItemSettingModel
    | undefined,
  fallback: string,
): string {
  return item?.Image?.AltText ?? item?.Title ?? fallback;
}

const generatedTechnologyRenderers: Record<
  string,
  GeneratedTechnologyRenderer
> = {
  [TechnologyAppearance.Home3.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeThreeTechnologySectionGenerated
        {...buildGeneratedSectionProps<HomeThreeTechnologySectionGeneratedProps>(
          "HomeThreeTechnologySection",
          {
            sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0Div0Div0Div0P0PText: renderHtmlValue(
              props.setting.Description,
            ),
            sectionDiv0Div1Div0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0Div1Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
              props,
              "technology",
            ),
            sectionDiv0Div0ImageWithBasePath1ImageSrc:
              getItemImageUrl(items[0]) ?? undefined,
            sectionDiv0Div0ImageWithBasePath1ImageAlt: getItemImageAlt(
              items[0],
              "technology",
            ),
            technologySectionTechnologyThrees: items.map((item, index) => ({
              key: item.Id,
              className:
                index === items.length - 1
                  ? "technology-item-three mb-0"
                  : "technology-item-three",
              src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/icons/tech-icon-1.svg",
              alt: getItemImageAlt(item, "tech-icon"),
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
            })),
          },
        )}
      />
    );
  },
  [TechnologyAppearance.Home13.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeThirteenTechnologySectionGenerated
        {...buildGeneratedSectionProps<HomeThirteenTechnologySectionGeneratedProps>(
          "HomeThirteenTechnologySection",
          {
            sectionDiv0Div0Div0Div0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0Div0Div0Div0ImageWithBasePath0ImageAlt:
              getSectionImageAlt(props, "technology"),
            sectionDiv0Div0Div0Div0ImageWithBasePath1ImageSrc:
              getItemImageUrl(items[0]) ?? undefined,
            sectionDiv0Div0Div0Div0ImageWithBasePath1ImageAlt: getItemImageAlt(
              items[0],
              "technology",
            ),
            sectionDiv0Div0Div1Div0H20H2Text:
              props.setting.Title ?? props.setting.Description ?? undefined,
            technologySectionDescriptions: [
              {
                key: "description",
                pText: renderHtmlValue(props.setting.Description),
              },
            ],
            technologySectionTechnologyThirteens: items.map((item) => ({
              key: item.Id,
              h30Text: item.Title ?? "",
              span0Text: item.Subtitle ?? "",
              p0Text: renderHtmlValue(item.Description),
            })),
          },
        )}
      />
    );
  },
};

const GeneratedTechnologySection: FC<TechnologySectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedTechnologyRenderers,
    TechnologyAppearance.Home3.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedTechnologySection;
